// Which top-level section the user came in through, for breadcrumbs.
//
// Every page under an item renders ItemBreadcrumbs, which used to hard-code
// "Items" as its first crumb. That was true when Items was the only way in;
// now the top-nav Trips page opens the same trip and logbook pages, and a
// breadcrumb trail that says "Items" would send the user somewhere they were
// never at.
//
// So the leading crumb is data, resolved in this order:
//
//   1. RootCrumbContext -- the page that ORIGINATES a non-default root wraps
//      its content in <RootCrumbProvider> (the Trips page does).
//   2. location.state.rootCrumb -- how that root travels to the next page.
//      useRootCrumbNavigate stamps it onto every push, so pages opt in by
//      swapping useNavigate() for it rather than threading a prop through.
//   3. Items -- the default, and what a top-nav click resets to (NavLink
//      pushes no state).
//
// Router state survives reloads, so a refreshed trip page keeps its trail.

import { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { urlForAllItems } from '../../fleet-shared/Urls.mjs';

export const defaultRootCrumb = { name: 'Items', url: urlForAllItems() };

export const RootCrumbContext = createContext(null);

export const RootCrumbProvider = ({ crumb, children }) => (
  <RootCrumbContext.Provider value={crumb ?? null}>{children}</RootCrumbContext.Provider>
);

// The crumb that leads the trail on this page.
export function useRootCrumb() {
  const fromContext = useContext(RootCrumbContext);
  const location = useLocation();
  const fromState = location.state?.rootCrumb;
  return useMemo(
    () => fromContext ?? fromState ?? defaultRootCrumb,
    [fromContext, fromState]
  );
}

// useNavigate(), but every push carries the current root crumb forward.
//
// Deliberately transparent otherwise: history deltas (navigate(-1)) and the
// default root pass straight through, so a page that adopts this hook behaves
// exactly as before until someone actually arrives from a non-default section.
export function useRootCrumbNavigate() {
  const navigate = useNavigate();
  const rootCrumb = useRootCrumb();
  const carry = rootCrumb !== defaultRootCrumb;

  return useCallback((to, options) => {
    if (!carry || typeof to === 'number') {
      return navigate(to, options);
    }
    return navigate(to, { ...options, state: { ...(options?.state ?? {}), rootCrumb } });
  }, [navigate, carry, rootCrumb]);
}
